import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const features = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    title: 'Control Académico Completo',
    description: 'Gestiona periodos, grupos, horarios y calificaciones desde un solo lugar.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Finanzas & Facturación CFDI',
    description: 'Contabilidad de doble entrada, cobranza y facturación electrónica CFDI 4.0.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Gestión de Alumnos 360°',
    description: 'Portal de autoservicio para alumnos, matrículas múltiples y seguimiento integral.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: 'IA para Generación de Horarios',
    description: 'Crea horarios inteligentes con Gemini AI respetando reglas y restricciones.',
  },
];

export default function TenantSelector() {
  const navigate = useNavigate();
  const [tenantSlug, setTenantSlug] = useState('');

  const knownTenants = [
    { slug: 'testcampus', name: 'TestCampus', description: 'Institución Educativa de Prueba' },
  ];

  const handleContinue = () => {
    if (tenantSlug.trim()) {
      navigate(`/${tenantSlug.toLowerCase().replace(/\s+/g, '-')}/login`);
    }
  };

  const handleSelectTenant = (slug: string) => {
    navigate(`/${slug}/login`);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* LEFT — Marketing panel */}
      <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 p-12 text-white">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -right-20 w-[32rem] h-[32rem] rounded-full bg-white/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-white/[0.03]" />

        {/* Logo + brand */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <img src="/qcampusone-logo.png" alt="QCampusOne" className="h-12 w-auto object-contain drop-shadow-lg" />
          </div>
          <p className="text-blue-200 text-sm font-medium tracking-wide uppercase">Sistema de Control Escolar</p>
        </div>

        {/* Hero text */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-6">
            Todo lo que tu institución necesita,{' '}
            <span className="text-blue-200">en un solo lugar</span>
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed mb-10 max-w-md">
            QCampusOne centraliza la administración académica, financiera y operativa de tu escuela con tecnología moderna y segura.
          </p>

          <div className="space-y-5">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-sm">
                  {f.icon}
                </div>
                <div>
                  <p className="font-semibold text-white">{f.title}</p>
                  <p className="text-blue-200 text-sm leading-snug">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer badge */}
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-blue-200 text-xs">Multi-institución · CFDI 4.0 · Seguridad JWT</span>
        </div>
      </div>

      {/* RIGHT — Tenant selector */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6 lg:p-16">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="flex lg:hidden justify-center mb-8">
            <img src="/qcampusone-logo.png" alt="QCampusOne" className="h-16 w-auto object-contain" />
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Selecciona tu institución</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Ingresa el identificador de tu escuela para continuar
            </p>
          </div>

          {/* Manual input */}
          <div className="space-y-3 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Identificador de institución
              </label>
              <Input
                id="tenant"
                type="text"
                placeholder="ej: mi-institucion"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleContinue()}
                className="h-11"
              />
            </div>
            <Button
              onClick={handleContinue}
              disabled={!tenantSlug.trim()}
              className="w-full h-11 text-base font-semibold"
            >
              Continuar
            </Button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-gray-50 dark:bg-gray-900 text-gray-400">O selecciona una institución</span>
            </div>
          </div>

          {/* Known tenants */}
          <div className="space-y-3">
            {knownTenants.map((tenant) => (
              <button
                key={tenant.slug}
                onClick={() => handleSelectTenant(tenant.slug)}
                className="w-full text-left p-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">{tenant.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{tenant.description}</div>
                  </div>
                  <svg className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              ← Regresar al inicio
            </button>
          </div>

          <p className="mt-6 text-center text-xs text-gray-400 dark:text-gray-600">
            Powered by <span className="font-semibold">QCampusOne</span> &mdash; Control Escolar Inteligente
          </p>
        </div>
      </div>
    </div>
  );
}
